import { collection, addDoc, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { buildForecastUrl, weatherCodeToCondition, getWeatherDescription } from '../utils/openMeteo';

interface WeatherData {
  id?: string;
  location: {
    lat: number;
    lng: number;
    name?: string;
  };
  conditions: {
    weather: string; // 'Sunny', 'Partly Cloudy', 'Cloudy', 'Rainy', 'Windy'
    temperature: number; // in Celsius
    humidity: number; // percentage
    windSpeed: number; // km/h
    description: string; // detailed description
  };
  timestamp: Timestamp;
  source: 'openmeteo' | 'manual' | 'cached';
}

interface OpenMeteoResponse {
  current: {
    temperature_2m: number;
    relative_humidity_2m: number;
    weather_code: number;
    wind_speed_10m: number;
  };
  location?: {
    name?: string;
  };
}

class WeatherService {
  private readonly CACHE_DURATION_HOURS = 1; // Cache weather data for 1 hour

  // Get cached weather data if available and recent
  async getCachedWeather(lat: number, lng: number): Promise<WeatherData | null> {
    try {
      const now = new Date();
      const oneHourAgo = new Date(now.getTime() - this.CACHE_DURATION_HOURS * 60 * 60 * 1000);

      const q = query(
        collection(db, 'weather_cache'),
        where('location.lat', '>=', lat - 0.01),
        where('location.lat', '<=', lat + 0.01),
        where('timestamp', '>', Timestamp.fromDate(oneHourAgo)),
        orderBy('timestamp', 'desc'),
        limit(1)
      );

      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() } as WeatherData;
      }
    } catch (error) {
      console.log('No cached weather found, will fetch fresh data');
    }
    return null;
  }

    // Fetch weather from Open-Meteo API
  async fetchWeatherFromAPI(lat: number, lng: number): Promise<WeatherData | null> {
    try {
      const url = buildForecastUrl(lat, lng);
      console.log('🌐 Fetching weather from Open-Meteo:', url);
      
      const response = await fetch(url);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Open-Meteo API error: ${response.status} - ${errorText}`);
        throw new Error(`Weather API error: ${response.status} - ${response.statusText}`);
      }

      const data: OpenMeteoResponse = await response.json();
      console.log('✅ Open-Meteo API response:', data);

      const weatherData: WeatherData = {
        location: {
          lat,
          lng,
          name: 'Location'
        },
        conditions: {
          weather: weatherCodeToCondition(data.current.weather_code),
          temperature: Math.round(data.current.temperature_2m),
          humidity: data.current.relative_humidity_2m,
          windSpeed: Math.round(data.current.wind_speed_10m),
          description: getWeatherDescription(data.current.weather_code)
        },
        timestamp: Timestamp.now(),
        source: 'openmeteo'
      };

      // Cache the weather data
      await this.cacheWeatherData(weatherData);

      return weatherData;
    } catch (error) {
      console.error('❌ Error fetching weather data from Open-Meteo:', error);
      return null;
    }
  }

  // Cache weather data in Firestore
  private async cacheWeatherData(weatherData: WeatherData): Promise<void> {
    try {
      await addDoc(collection(db, 'weather_cache'), weatherData);
      console.log('Weather data cached successfully');
    } catch (error) {
      console.error('Error caching weather data:', error);
    }
  }

  // Get weather data - tries cache first, then API, then fallback
  async getWeatherData(lat: number, lng: number): Promise<WeatherData | null> {
    console.log(`🌤️ Getting weather data for location: ${lat}, ${lng}`);

    // First try to get cached data
    const cachedData = await this.getCachedWeather(lat, lng);
    if (cachedData) {
      console.log('📦 Using cached weather data');
      return cachedData;
    }

    // If no cached data, try to fetch from API
    console.log('🌐 Fetching fresh weather data from Open-Meteo API');
    const apiData = await this.fetchWeatherFromAPI(lat, lng);
    if (apiData) {
      return apiData;
    }

    // Fallback: provide reasonable default weather for UK
    console.log('🔄 Using fallback weather estimation');
    return this.getFallbackWeather(lat, lng);
  }

  // Provide reasonable fallback weather for UK locations
  private getFallbackWeather(lat: number, lng: number): WeatherData {
    // Basic weather estimation for UK climate
    const now = new Date();
    const month = now.getMonth(); // 0-11
    const hour = now.getHours();
    
    // Estimate temperature based on UK seasonal averages
    let temperature: number;
    if (month >= 5 && month <= 7) { // Jun-Aug (summer)
      temperature = hour < 6 || hour > 20 ? 15 : 20; // Cooler at night
    } else if (month >= 8 && month <= 10) { // Sep-Nov (autumn)
      temperature = hour < 6 || hour > 20 ? 8 : 15;
    } else if (month >= 11 || month <= 2) { // Dec-Feb (winter)
      temperature = hour < 6 || hour > 20 ? 2 : 8;
    } else { // Mar-May (spring)
      temperature = hour < 6 || hour > 20 ? 8 : 15;
    }

    // Default to partly cloudy (most common UK weather)
    const weather = 'Partly Cloudy';

    return {
      location: { lat, lng, name: 'UK Location' },
      conditions: {
        weather,
        temperature,
        humidity: 70, // Typical UK humidity
        windSpeed: 15, // Typical UK wind speed
        description: 'estimated conditions'
      },
      timestamp: Timestamp.now(),
      source: 'manual'
    };
  }

  // Get weather for inspection form (simplified interface)
  async getWeatherForInspection(lat: number, lng: number): Promise<{
    weather: string;
    temperature: string;
    success: boolean;
    source: string;
  }> {
    try {
      const weatherData = await this.getWeatherData(lat, lng);
      
      if (weatherData) {
        const sourceLabel = weatherData.source === 'openmeteo' ? 'API' :
                           weatherData.source === 'manual' ? 'Estimated' : 'Cached';
        
        return {
          weather: weatherData.conditions.weather,
          temperature: weatherData.conditions.temperature.toString(),
          success: true,
          source: sourceLabel
        };
      } else {
        // This should not happen with the fallback, but just in case
        return {
          weather: 'Partly Cloudy',
          temperature: '15',
          success: true,
          source: 'Default'
        };
      }
    } catch (error) {
      console.error('Error getting weather for inspection:', error);
      // Even on error, provide a reasonable default
      return {
        weather: 'Partly Cloudy',
        temperature: '15',
        success: true,
        source: 'Default'
      };
    }
  }
}

export const weatherService = new WeatherService();
export type { WeatherData };
